/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { useState } from 'react'
import LockIcon from '@mui/icons-material/Lock'
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate'
import InfoIcon from '@mui/icons-material/Info'
import { OnChangeEventContext } from 'react-crane'

import {
  BoardGroupFragment,
  CatalogSearchResultFragment,
  PlanItemDetailsFragment
} from '../../../../generated/graphql'
import { Banner, WarningBanner } from '../../../shared/alerts'
import PlaceholderFields from '../PlaceholderFields'
import NonCourseFields from '../NonCourseFields'
import CourseAutoComplete from '../../../courses/CourseAutoComplete'
import CourseOptionLabel from '../../../courses/CourseOptionLabel'
import { subtitle1Css } from '../../../../lib/styles/typography'
import { lightBlueBackground } from '../../../../lib/styles/colors'
import { Select } from '../../../shared/input'
import CourseForm from '../CourseForm'
import { DetailsPlanAlert } from '../../../../types'
import PlanItemAlerts from '../PlanItemAlerts'
import { stringFormat } from '../../../../lib/utils'

type NonCourseFormProps = {
  /** Label to use for advisor */
  advisorLabel: string
  /** List of alerts associated the item ready to display */
  alerts: Array<DetailsPlanAlert>
  /** Campus used to filter courses */
  campus?: string
  /** Label to use for campus */
  campusLabel: string
  /** The format for how to display the course title */
  courseDisplayFormat: string
  /** Label to use for course */
  courseLabel: string
  /** Label to use for credits */
  creditsLabel: string
  /** Minimum number of credits for the placeholder */
  creditsMin?: number | undefined
  /** Maximum number of credits for the placeholder */
  creditsMax?: number | undefined
  /** Description of Plan Item */
  description: string | undefined
  /** Whether all of the inputs and buttons should be disabled */
  readOnly: boolean
  /** Current GroupId of Plan Item */
  groupId: string | null | undefined
  /** Flag to determine whether or not to display the Requisite(s) label as error text */
  hasRequisiteError?: boolean
  /** Whether the plan item is a placeholder that the student must choose a course for as it is currently saved in the db */
  isCoursePlaceholder: boolean
  /** The current selection of isCoursePlaceholder persisted in state */
  isCoursePlaceholderState: boolean
  /** Whether the user is a student. */
  isStudent: boolean
  /** Whether the item has been locked by an admin */
  locked: boolean
  /** Event callback input/select in form has changed  */
  onChange: (
    name: string,
    value:
      | string
      | BoardGroupFragment
      | CatalogSearchResultFragment
      | CatalogSearchResultFragment[]
      | boolean
      | number
      | undefined
  ) => void
  /** Event callback when the delete button is clicked in the Alert Banner */
  onDelete?: () => void
  /** Event callback when the dismiss button is clicked in the Alert Banner */
  onDismissAlert?: (alert: DetailsPlanAlert) => void
  /** Callback when the isCoursePlaceholder switch changes */
  onIsCoursePlaceholderChange: () => void
  /** Event callback when a placeholder course is selected from the drop down list of options */
  onPlaceholderCourseSelected: (selectedCourse: CatalogSearchResultFragment | undefined) => void
  /** All Plan Groups for Current Plan */
  planGroups: Array<BoardGroupFragment>
  /** The Current Plan Item */
  planItem: PlanItemDetailsFragment
  /** Whether the item has been required by an admin */
  required: boolean
  /** Selected course for placeholder */
  selectedCourse: CatalogSearchResultFragment | undefined
  /** Selected course for placeholder */
  selectedCoursePlaceholders: CatalogSearchResultFragment[] | undefined
  /** Label to use for term */
  termLabel: string
  /** title of Plan Item */
  title: string
}

const defaultProps: Pick<
  NonCourseFormProps,
  'campus' | 'creditsMin' | 'creditsMax' | 'onDelete' | 'onDismissAlert' | 'hasRequisiteError'
> = {
  campus: undefined,
  creditsMin: undefined,
  creditsMax: undefined,
  hasRequisiteError: false,
  onDelete: undefined,
  onDismissAlert: undefined
}

const formCss = css`
  margin-left: -8px;
  margin-right: -8px;
`
const selectCourseWrapperCss = css`
  margin: 0 8px;
`
const addCourseSubtitleCss = css`
  padding-bottom: 8px;
  padding-top: 12px;
  margin-top: 16px;
  ${subtitle1Css}
`
const bannerContainerCss = css`
  grid-column: 1 / 3;
  padding-bottom: 11px;

  .requiredBanner {
    justify-content: left;
    justify-items: left;
  }
`
const placeholderBannerCss = css`
  grid-column: 1 / 3;
  margin-bottom: 12px;
`
const selectCss = css`
  margin-left: 0;
  margin-right: 0;
`

export const NonCourseForm = ({
  advisorLabel,
  alerts,
  campus,
  campusLabel,
  courseDisplayFormat,
  courseLabel,
  creditsLabel,
  creditsMax,
  creditsMin,
  description,
  groupId,
  hasRequisiteError,
  isCoursePlaceholder,
  isCoursePlaceholderState,
  isStudent,
  locked,
  onChange,
  onDelete,
  onDismissAlert,
  onIsCoursePlaceholderChange,
  onPlaceholderCourseSelected,
  planGroups,
  planItem,
  readOnly,
  required,
  selectedCourse,
  selectedCoursePlaceholders,
  termLabel,
  title
}: NonCourseFormProps) => {
  const [editPlaceholdersMode, setEditPlaceholdersMode] = useState(false)
  const fields =
    isStudent && isCoursePlaceholder ? (
      <PlaceholderFields
        creditsLabel={creditsLabel}
        creditsMax={creditsMax}
        creditsMin={creditsMin}
        description={description}
        groupId={groupId}
        locked={locked}
        onChange={onChange}
        planGroups={planGroups}
        readOnly={readOnly}
        termLabel={termLabel}
        title={title}
      />
    ) : (
      <NonCourseFields
        campus={campus}
        courseLabel={courseLabel}
        creditsLabel={creditsLabel}
        creditsMax={creditsMax}
        creditsMin={creditsMin}
        description={description}
        editPlaceholdersMode={editPlaceholdersMode}
        groupId={groupId}
        isCoursePlaceholder={isCoursePlaceholder}
        isCoursePlaceholderState={isCoursePlaceholderState}
        isStudent={isStudent}
        locked={locked}
        onChange={onChange}
        onEditPlaceholdersModeChange={setEditPlaceholdersMode}
        onIsCoursePlaceholderChange={onIsCoursePlaceholderChange}
        planGroups={planGroups}
        readOnly={readOnly}
        required={required}
        selectedCourse={selectedCourse}
        selectedCoursePlaceholders={selectedCoursePlaceholders}
        termLabel={termLabel}
        title={title}
      />
    )

  // In this context, should only ever have one course selected (array type option is necessary to make typescript happy)
  const handleCourseChange = (
    course: CatalogSearchResultFragment | CatalogSearchResultFragment[]
  ) => {
    onChange('selectedCourse', course)
    onPlaceholderCourseSelected(course as CatalogSearchResultFragment)
  }

  const handleCourseSelect = (e: OnChangeEventContext) => {
    onChange('selectedCourse', e.value as CatalogSearchResultFragment)
    onPlaceholderCourseSelected(e.value as CatalogSearchResultFragment)
  }

  const handleCourseClear = () => {
    onChange('selectedCourse', '')
    onPlaceholderCourseSelected(undefined)
  }

  const placeholderText = `Select ${courseLabel.toLowerCase()} to fulfill this placeholder`
  const options = selectedCoursePlaceholders?.map((c) => ({
    ...c,
    uniqueKey: `${c.externalCourseId}`,
    label: c ? stringFormat(courseDisplayFormat, c) : ''
  }))

  return (
    <div css={formCss}>
      <PlanItemAlerts
        alerts={alerts}
        courseLabel={courseLabel.toLowerCase()}
        isCoursePlaceholder={isCoursePlaceholder}
        locked={locked}
        onClear={handleCourseClear}
        onDelete={onDelete}
        onDismissAlert={onDismissAlert}
        readOnly={readOnly}
        required={required}
      />
      {isStudent && locked && (
        <div css={bannerContainerCss}>
          <Banner icon={<LockIcon />} backgroundColor={lightBlueBackground}>
            This item is locked on your plan by your {advisorLabel.toLowerCase()}. You cannot move
            it to another {termLabel.toLowerCase()}.
          </Banner>
        </div>
      )}
      {isStudent && required && (
        <div css={bannerContainerCss}>
          <Banner
            icon={<AssignmentLateIcon />}
            backgroundColor={lightBlueBackground}
            className="requiredBanner"
          >
            This item is required and cannot be deleted.
          </Banner>
        </div>
      )}
      {isCoursePlaceholder && (
        <div css={placeholderBannerCss}>
          <WarningBanner icon={<InfoIcon />}>
            This is a {courseLabel.toLowerCase()} placeholder. Select a {courseLabel.toLowerCase()}{' '}
            for this placeholder from the dropdown.
          </WarningBanner>
        </div>
      )}
      {fields}
      {isCoursePlaceholder && isCoursePlaceholderState && !editPlaceholdersMode && (
        <div css={selectCourseWrapperCss}>
          <div css={addCourseSubtitleCss}>{`Select a ${courseLabel}`}</div>
          {selectedCoursePlaceholders?.length ? (
            <Select
              aria-label={placeholderText}
              clearable
              css={selectCss}
              disabled={readOnly}
              id="course-selection"
              label={courseLabel}
              labelKey="label"
              name="course-selection"
              onChange={handleCourseSelect}
              optionRenderer={CourseOptionLabel}
              options={options}
              placeholder={placeholderText}
              valueKey="externalCourseId"
              value={selectedCourse?.externalCourseId}
            />
          ) : (
            <CourseAutoComplete
              clearable
              disabled={readOnly}
              groupId={groupId || ''}
              label={courseLabel}
              placeholder={placeholderText}
              onChange={handleCourseChange}
              selectedCourse={selectedCourse}
              value={selectedCourse?.externalCourseId}
            />
          )}
          {selectedCourse && (
            <CourseForm
              alerts={[]}
              campusLabel={campusLabel}
              courseLabel={courseLabel}
              creditsLabel={creditsLabel}
              groupId={groupId}
              hasRequisiteError={hasRequisiteError}
              isCoursePlaceholderSelection
              isStudent={isStudent}
              locked={locked}
              onChange={onChange}
              planGroups={planGroups}
              planItem={planItem}
              readOnly={readOnly}
              required={required}
              termLabel={termLabel}
            />
          )}
        </div>
      )}
    </div>
  )
}

NonCourseForm.defaultProps = defaultProps

export default NonCourseForm
